// The Creatures page: storage that scales (search, grade and roll filters,
// sort, grid or list, multi-select with bulk favorite, add to team and
// release, and a clean-up helper), plus the Team tab with the team order and
// Shrines as cards with slot pickers. Everything here is active play; the hunt
// runs on the server whatever this page shows.
import { ascendMark } from './ascend.ts';
import { huntPower } from '../shared/power.ts';
import { CONFIG } from '../shared/config.ts';
import { SPECIES } from '../shared/species.ts';
import { ELEMENTS, ELEMENT_IDS } from '../shared/elements.ts';
import { GROUND, AREA, REGIONS } from '../shared/grounds.ts';
import { traitOf } from '../shared/traits.ts';
import { SHRINE_TIERS, SHRINES_IN_USE, SHARDS_PER_SHRINE, SHRINES_PER_FUSE, shrineChance, type Shrine } from '../shared/shrines.ts';
import { killXp } from '../shared/xp.ts';
import { ratingOf } from '../shared/rating.ts';
import { ivPercent } from '../shared/rolls.ts';
import { statWeights } from '../shared/rating.ts';
import { computeStats, creatureStats } from '../shared/stats.ts';
import { gradeOf, GRADE_CUTOFFS, type Grade } from '../shared/grade.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { openTeamBuilder } from './teambuilder.ts';
import { asFighter } from '../shared/social.ts';
import { spriteUrl, iconUrl } from './assets.ts';
import { myArt } from './skins.ts';
import { esc, full, elChip, cname, nameHtml, qualityEffect } from './fmt.ts';
import { nameMatches, speciesName } from '../shared/nametags.ts';
import { gradeBadge, cardFlags, crest, auraTag, isFavorite, favStar } from './badges.ts';
import { rollParts, miniPills, type RollPart } from './rollpills.ts';
import { openDialog, toast } from './dialog.ts';
import { KEPT_WHAT, GRADE_ORDER, bulkReleaseList, type BulkRelease } from '../shared/automation.ts';
import { ALTAR, altarLeft, altarOdds, offerable, worstOfferable } from '../shared/altar.ts';
import { ITEM } from '../shared/items.ts';
import { xpSource, leadCreature } from './hud.ts';
import { ICON } from './icons.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
let listCreature: (id: number) => void = () => {};
export function bindCreatures(o: { send: (m: ClientMsg) => void; render: () => void; list: (id: number) => void }) {
  send = o.send; rerender = o.render; listCreature = o.list;
}

// ---------------- per-creature numbers, cached ----------------
interface Facts { score: number; grade: Grade; power: number; talent: number; quality: number }
const facts = new Map<number, { key: string; f: Facts }>();
export function factsOf(c: CreatureDTO): Facts {
  const key = `${c.species}:${c.level}:${c.stars}:${c.refine?.join('.') ?? ''}:${c.train?.join('.') ?? ''}`;
  const hit = facts.get(c.id);
  if (hit && hit.key === key) return hit.f;
  const score = ratingOf(c.rolls, c.species);
  const f = { score, grade: gradeOf(score), power: huntPower(c), talent: ivPercent(c.rolls, statWeights(c.species)), quality: c.rolls.quality };
  facts.set(c.id, { key, f });
  return f;
}
/** Coins a release pays. */
export const releaseCoins = (c: CreatureDTO) => 5 + 2 * c.level;

// ---------------- filters and sort ----------------
type SortKey = 'recent' | 'grade' | 'level' | 'power' | 'species' | 'bloodline' | 'name' | 'talent' | 'quality' | 'aura' | 'trait' | 'elements' | 'caught';
const SORTS: [SortKey, string][] = [['recent', 'Newest first'], ['grade', 'Grade'], ['talent', 'Talent'], ['quality', 'Quality'], ['bloodline', 'Bloodline'], ['aura', 'Aura first'], ['power', 'Power'], ['level', 'Level'], ['species', 'Species']];
const F = {
  grades: new Set<Grade>(),
  /** 0 = any; 1 = II and up, 2 = III and up, 3 = IV and up (0-based roll). */
  blMin: 0,
  aura: false,
  fav: false,
  species: '',
  element: '',
  /** Only creatures caught at or after this time (0 = any): "See new catches" on the away card. */
  since: 0,
  sort: 'recent' as SortKey,
  /** List headers flip the direction. */
  asc: false,
};
let view: 'grid' | 'list' = (() => { try { return localStorage.getItem('mi-creatures-view') === 'list' ? 'list' : 'grid'; } catch { return 'grid'; } })();
let selecting = false;
/** Phones: the filters beyond search fold behind one button. */
let filtersOpen = false;
const selected = new Set<number>();
let anchorId: number | null = null;

export const sortLabel = () => (SORTS.find(([v]) => v === F.sort)?.[1] ?? 'Sorted').toLowerCase();

/** The page's search: a name, a species or an element (`q` lower case). */
const searchMatches = (c: CreatureDTO, q: string) =>
  nameMatches(c, q) || c.species.includes(q) || SPECIES[c.species].elements.some((e) => e.includes(q) || ELEMENTS[e].name.toLowerCase().includes(q));

/** The creatures the page shows, in its current filters and sort; the creature page's pager walks the same list. */
export function depotList(): CreatureDTO[] {
  const t = S.trainer!;
  let list = [...S.creatures.values()];
  if (S.depotView === 'collection') { const col = new Set(t.collection ?? []); list = list.filter((c) => col.has(c.id)); }
  if (S.depotView === 'listed') list = list.filter((c) => c.listed);
  const q = S.depotFilter.trim().toLowerCase();
  if (q) list = list.filter((c) => searchMatches(c, q));
  if (F.grades.size) list = list.filter((c) => F.grades.has(factsOf(c).grade));
  if (F.blMin) list = list.filter((c) => c.rolls.bloodline >= F.blMin);
  if (F.aura) list = list.filter((c) => c.rolls.aura);
  if (F.fav) list = list.filter(isFavorite);
  if (F.species) list = list.filter((c) => c.species === F.species);
  if (F.element) list = list.filter((c) => (SPECIES[c.species].elements as string[]).includes(F.element));
  if (F.since) list = list.filter((c) => c.caughtAt >= F.since);
  const dir = F.asc ? -1 : 1;
  const by: Record<SortKey, (a: CreatureDTO, b: CreatureDTO) => number> = {
    recent: (a, b) => b.caughtAt - a.caughtAt || b.id - a.id,
    grade: (a, b) => factsOf(b).score - factsOf(a).score || factsOf(b).power - factsOf(a).power,
    level: (a, b) => b.level - a.level,
    power: (a, b) => factsOf(b).power - factsOf(a).power,
    species: (a, b) => speciesName(a).localeCompare(speciesName(b)) || factsOf(b).score - factsOf(a).score,
    name: (a, b) => cname(b).localeCompare(cname(a)),
    bloodline: (a, b) => b.rolls.bloodline - a.rolls.bloodline || factsOf(b).score - factsOf(a).score,
    talent: (a, b) => factsOf(b).talent - factsOf(a).talent || factsOf(b).score - factsOf(a).score,
    quality: (a, b) => b.rolls.quality - a.rolls.quality || factsOf(b).score - factsOf(a).score,
    aura: (a, b) => Number(b.rolls.aura) - Number(a.rolls.aura) || factsOf(b).score - factsOf(a).score,
    trait: (a, b) => (traitOf(b)?.name ?? '').localeCompare(traitOf(a)?.name ?? ''),
    elements: (a, b) => SPECIES[b.species].elements.join().localeCompare(SPECIES[a.species].elements.join()),
    caught: (a, b) => b.caughtAt - a.caughtAt,
  };
  const cmp = by[F.sort];
  // Species and name read A to Z by default.
  const flip = F.sort === 'species' ? 1 : F.sort === 'name' ? -1 : 1;
  return list.sort((a, b) => cmp(a, b) * dir * flip || b.id - a.id);
}

/** What release can never take: favorites, the team, listings and the starter. */
function releasable(c: CreatureDTO) {
  const t = S.trainer!;
  return !isFavorite(c) && !t.team.includes(c.id) && !c.listed && !c.bound;
}

// ---------------- cards and rows ----------------
function caughtWhen(c: CreatureDTO) {
  const d = new Date(c.caughtAt), today = new Date();
  const days = Math.round((new Date(today.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000);
  const when = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const g = GROUND[c.ground];
  return `${when}${g ? `, ${AREA[g.area].name}` : c.ground.startsWith('boss') ? ', boss fight' : ''}`;
}

export function card(c: CreatureDTO) {
  const f = factsOf(c);
  const t = S.trainer!;
  const sel = selected.has(c.id);
  // The rolls show as the same colored pills as the team rail; the corner keeps only the favorite star.
  return `<div class="card${c.rolls.aura ? ' aura' : ''}${sel ? ' sel' : ''}" data-open="${c.id}" data-key="c${c.id}" data-grade="${f.grade}">
    <span class="tr">${isFavorite(c) ? favStar() : ''}</span>
    <img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt="" loading="lazy">
    <div class="nm">${nameHtml(c)}${c.stars ? ` <span class="stars">${'★'.repeat(c.stars)}</span>` : ''}</div>
    <div class="lv">Lv ${c.level}${ascendMark(c)}${c.listed ? ' · For sale' : ''}</div>
    ${miniPills(c)}
    ${t.team.includes(c.id) ? '<span class="teamtag">TEAM</span>' : ''}
    ${selecting ? `<span class="pick${sel ? ' on' : ''}" aria-hidden="true"></span>` : ''}</div>`;
}

function row(c: CreatureDTO) {
  const f = factsOf(c);
  const sel = selected.has(c.id);
  const parts = Object.fromEntries(rollParts(c).map((p) => [p.key, p])) as Partial<Record<RollPart['key'], RollPart>>;
  return `<tr class="${sel ? 'sel' : ''}" data-open="${c.id}" data-key="c${c.id}">
    <td>${selecting ? `<span class="pick${sel ? ' on' : ''}"></span>` : ''}</td>
    <td><img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt="" loading="lazy"></td>
    <td class="nm">${nameHtml(c)}${isFavorite(c) ? ' <span class="fav">★</span>' : ''}${S.trainer!.team.includes(c.id) ? ' <span class="teamtag">TEAM</span>' : ''}${c.listed ? ' <span class="muted">for sale</span>' : ''}</td>
    <td class="num">${ascendMark(c)}${c.level}</td>
    <td>${cell(parts.grade)}</td>
    <td>${cell(parts.talent)}</td>
    <td>${cell(parts.quality)}</td>
    <td>${cell(parts.bloodline)}</td>
    <td>${parts.aura ? cell(parts.aura) : ''}</td>
    <td class="num">${full(f.power)}</td>
    <td>${parts.trait ? cell(parts.trait) : ''}</td>
    <td>${SPECIES[c.species].elements.map(elChip).join(' ')}</td>
    <td class="muted">${esc(caughtWhen(c))}</td></tr>`;
}

/** One roll as a colored pill in a list cell, its details on hover. */
const cell = (p: RollPart | undefined) => (p ? `<span class="rp t-${p.tier}" tabindex="0" data-tip="${esc(p.tip)}">${p.value}</span>` : '');

// The grid (or list) pages instead of scrolling (Cole, 2026-10-27): only one page of creatures renders, sized to
// the room the screen leaves. The first render guesses; creaturesFit() measures and asks for one more render.
const CARD_W = 124, GAP = 8;
let lastCount = 0;
let cPage = 0, cSig = '';
/** Rows of cards (or table rows) per page and cards per row, as last measured. */
let fitRows = 0, fitCols = 0;
const PAGER_H = 44;
function perRow() {
  const host = document.getElementById('cgrid');
  const w = host?.clientWidth ?? 1200;
  return Math.max(1, Math.floor((w + GAP) / (CARD_W + GAP)));
}
/** How many fit on a page: measured rows times cards per row (a guess before the first measure). */
function perPage(): number {
  if (view === 'list') return Math.max(3, fitRows || 8);
  return Math.max(1, (fitRows || 3) * (fitCols || perRow()));
}
/** The page of the list to show: [first, end, page, pages]. A new filter or sort goes back to page 1. */
function pageOf(n: number): [number, number, number, number] {
  const size = perPage(), pages = Math.max(1, Math.ceil(n / size));
  const sig = `${F.sort}:${F.asc}:${S.depotView}:${S.depotFilter}:${[...F.grades].join()}:${F.blMin}:${F.aura}:${F.fav}:${F.species}:${F.element}:${F.since}:${view}`;
  if (sig !== cSig) { cSig = sig; cPage = 0; }
  cPage = Math.min(Math.max(0, cPage), pages - 1);
  return [cPage * size, Math.min(n, (cPage + 1) * size), cPage, pages];
}
const pagerHtml = (p: number, pages: number) => pages < 2 ? '' : `<div class="fit-pager cpager"><button class="btn small" data-act="cf-page" data-id="${p - 1}"${p ? '' : ' disabled'} aria-label="Previous page">‹</button><span class="fit-n">${p + 1} / ${pages}</span><button class="btn small" data-act="cf-page" data-id="${p + 1}"${p + 1 < pages ? '' : ' disabled'} aria-label="Next page">›</button></div>`;

/**
 * After a render: measure how many rows fit between the list's top and the bottom of the screen. True when that
 * changed, so the caller renders once more with the right page size.
 */
export function creaturesFit(): boolean {
  if (S.page !== 'creatures' || S.tab !== 'depot') return false;
  const host = document.getElementById('cgrid');
  if (!host) return false;
  const items = [...host.querySelectorAll<HTMLElement>(view === 'list' ? 'tbody > tr' : ':scope > .card')];
  if (!items.length) return false;
  const nav = document.getElementById('bnav');
  const navTop = nav && nav.getBoundingClientRect().height ? nav.getBoundingClientRect().top : innerHeight;
  const bar = document.querySelector('.selbar')?.getBoundingClientRect();
  const bottom = Math.min(innerHeight, navTop, bar?.height ? bar.top : innerHeight) - 12;
  const first = items[0].getBoundingClientRect();
  const top = view === 'list' ? first.top : host.getBoundingClientRect().top;
  const rowH = first.height + (view === 'list' ? 0 : GAP);
  // Columns come from the grid's own tracks, not from the cards on the first row: the last page can hold a short row,
  // and counting that sent page 3 of 68 back and forth between two page sizes forever (Jonny, 2026-10-29).
  const tracks = getComputedStyle(host).gridTemplateColumns.split(' ').filter((x) => parseFloat(x) > 0).length;
  const cols = view === 'list' ? 1 : tracks || perRow();
  const room = bottom - top - PAGER_H + (view === 'list' ? 0 : GAP);
  const rows = Math.max(1, Math.floor(room / rowH));
  if (rows === fitRows && cols === fitCols) return false;
  fitRows = rows; fitCols = cols;
  return true;
}
/** Kept for the old scroll hook: the list never needs a re-render on scroll now. */

// ---------------- the page ----------------
export function creaturesHead() {
  const t = S.trainer!;
  const all = S.creatures.size, fav = (t.collection ?? []).length, sale = [...S.creatures.values()].filter((c) => c.listed).length;
  const sub = S.tab === 'team' ? 'team' : S.depotView === 'collection' ? 'favorites' : S.depotView === 'listed' ? 'for-sale' : '';
  return {
    meta: '',
    tabs: `<nav class="subtabs">${[['', 'All', all], ['team', 'Team', t.team.length], ['favorites', 'Favorites', fav], ['for-sale', 'For sale', sale]].map(([id, n, k]) => `<a href="#/creatures${id ? `/${id}` : ''}" class="${sub === id ? 'on' : ''}">${n} <span class="muted">${full(Number(k))}</span></a>`).join('')}</nav>`,
    actions: '<span class="cr-acts"><button class="btn small" data-act="bulk-release"><span class="cl">Release creatures…</span><span class="cs">Release</span></button><button class="btn small" data-act="altar">Altar…</button><a class="btn small" href="#/calculator"><span class="cl">Grade calculator</span><span class="cs">Calculator</span></a></span>',
  };
}

function filterBar(total: number) {
  const counts = new Map<string, number>();
  for (const c of S.creatures.values()) counts.set(c.species, (counts.get(c.species) ?? 0) + 1);
  const speciesOpts = REGIONS.map((r) => {
    const ids = Object.values(SPECIES).filter((sp) => sp.region === r.id && counts.has(sp.id)).sort((a, b) => a.name.localeCompare(b.name));
    return ids.length ? `<optgroup label="${r.name}">${ids.map((sp) => `<option value="${sp.id}"${F.species === sp.id ? ' selected' : ''}>${esc(sp.name)} (${counts.get(sp.id)})</option>`).join('')}</optgroup>` : '';
  }).join('');
  const on = F.grades.size + (F.blMin ? 1 : 0) + (F.aura ? 1 : 0) + (F.fav ? 1 : 0) + (F.species ? 1 : 0) + (F.element ? 1 : 0);
  return `<div class="fbar${filtersOpen ? ' open' : ''}">
    <button class="btn small fmore${filtersOpen ? ' on' : ''}" data-act="cf-more" aria-expanded="${filtersOpen}">Filters${on ? ` <b>${on}</b>` : ''}</button>
    <label class="fsearch">${ICON.search}<input data-depot-filter placeholder="Name, species or element" value="${esc(S.depotFilter)}" aria-label="Search by name, species or element"></label>
    <div class="gseg" role="group" aria-label="Grade"><span>Grade</span>${(['C', 'B', 'A', 'S', 'S+'] as Grade[]).map((g) => `<button class="${F.grades.has(g) ? 'on' : ''}" data-act="cf-grade" data-id="${g}" aria-pressed="${F.grades.has(g)}">${gradeBadge(GRADE_CUTOFFS.find(([x]) => x === g)![1], 'sm')}</button>`).join('')}</div>
    <select data-cf="blMin" aria-label="Bloodline">${[0, 1, 2, 3].map((v) => [v, v ? `Bloodline ${CONFIG.bloodline[v].name}+` : 'Any bloodline'] as const).map(([v, n]) => `<option value="${v}"${F.blMin === v ? ' selected' : ''}>${n}</option>`).join('')}</select>
    <button class="btn small tog${F.aura ? ' on' : ''}" data-act="cf-aura" aria-pressed="${F.aura}">✦ Aura</button>
    <button class="btn small tog${F.fav ? ' on' : ''}" data-act="cf-fav" aria-pressed="${F.fav}">★ Favorites</button>
    <select data-cf="species" aria-label="Species"><option value="">All species</option>${speciesOpts}</select>
    <select data-cf="element" aria-label="Element"><option value="">All elements</option>${ELEMENT_IDS.map((e) => `<option value="${e}"${F.element === e ? ' selected' : ''}>${ELEMENTS[e].name}</option>`).join('')}</select>
    <span class="spacer"></span>
    <label class="muted fsort">Sort <select data-cf="sort" aria-label="Sort">${SORTS.map(([v, n]) => `<option value="${v}"${F.sort === v ? ' selected' : ''}>${n}</option>`).join('')}${SORTS.some(([v]) => v === F.sort) ? '' : `<option value="${F.sort}" selected>By column</option>`}</select></label>
    <div class="vseg" role="group" aria-label="View"><button class="${view === 'grid' ? 'on' : ''}" data-act="cf-view" data-id="grid" aria-label="Grid view">${ICON.gridview}</button><button class="${view === 'list' ? 'on' : ''}" data-act="cf-view" data-id="list" aria-label="List view">${ICON.listview}</button></div>
    <button class="btn small" data-act="cf-select">${selecting ? 'Done selecting' : 'Select'}</button>
  </div>
  ${F.since ? `<p class="hint">Caught since ${new Date(F.since).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} <button class="linkbtn" data-act="cf-since">Show all</button></p>` : ''}
  ${F.grades.size || F.blMin || F.aura || F.fav || F.species || F.element || F.since || S.depotFilter ? `<p class="hint">${full(total)} match. <button class="linkbtn" data-act="cf-clear">Clear filters</button></p>` : ''}`;
}

function selectionBar() {
  if (!selecting || !selected.size) return selecting ? `<div class="selbar"><span>0 selected</span><button class="btn small" data-act="sel-all">Select all</button><button class="btn small" data-act="cf-select">Done</button></div>` : '';
  const t = S.trainer!;
  const picked = [...selected].map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  const rel = picked.filter(releasable);
  const coins = rel.reduce((a, c) => a + releaseCoins(c), 0);
  const free = CONFIG.teamSize - t.team.length;
  const addable = picked.filter((c) => !t.team.includes(c.id) && !c.listed);
  const allFav = picked.every(isFavorite);
  const one = picked.length === 1 ? picked[0] : null;
  const listWhy = !one ? 'One at a time' : isFavorite(one) ? 'Favorite' : t.team.includes(one.id) ? 'On your team' : one.listed ? 'Already listed' : one.devGranted ? 'Admin-made' : one.bound ? 'Your starter' : '';
  return `<div class="selbar"><b>${picked.length} selected</b>
    <button class="btn small" data-act="sel-fav">${allFav ? '★ Remove favorite' : '★ Favorite'}</button>
    <button class="btn small" data-act="sel-team"${free > 0 && addable.length ? '' : ' disabled'}>${free > 0 ? 'Add to team' : 'Team full'}</button>
    <button class="btn small" data-act="sel-list"${listWhy ? ` disabled title="${listWhy}"` : ''}>List for sale</button>
    <button class="btn small" data-act="sel-altar"${altarPick(picked).length === picked.length && picked.length <= ALTAR.slots ? '' : ' disabled'}>Altar</button>
    <button class="btn small danger" data-act="sel-release"${rel.length ? '' : ' disabled'}>${rel.length ? `Release for ${full(coins)} coins` : 'Nothing to release'}</button>
    <button class="btn small" data-act="sel-clear">Clear</button></div>`;
}

export function creaturesPane(): string {
  const list = depotList();
  lastCount = list.length;
  const empty = `<p class="hint">${S.depotView === 'listed' ? 'Nothing listed.' : S.depotView === 'collection' ? 'No favorites.' : 'Nothing matches.'}</p>`;
  let body: string;
  if (view === 'list') {
    const [a, b, p, pages] = pageOf(list.length);
    const th = (k: SortKey, n: string, cls = '') => `<th class="${cls}"><button data-act="cf-col" data-id="${k}">${n}${F.sort === k ? (F.asc ? ' ▴' : ' ▾') : ''}</button></th>`;
    body = list.length ? `<div class="ctable-wrap"><table class="ctable" id="cgrid"><thead><tr><th></th><th></th>${th('name', 'Name')}${th('level', 'Lv', 'num')}${th('grade', 'Grade')}${th('talent', 'Talent')}${th('quality', 'Quality')}${th('bloodline', 'Bloodline')}${th('aura', 'Aura')}${th('power', 'Power', 'num')}${th('trait', 'Trait')}${th('elements', 'Elements')}${th('caught', 'Caught')}</tr></thead>
      <tbody>${list.slice(a, b).map(row).join('')}</tbody></table></div>${pagerHtml(p, pages)}` : empty;
  } else {
    const [a, b, p, pages] = pageOf(list.length);
    body = list.length ? `<div class="cgrid${selecting ? ' selecting' : ''}" id="cgrid">${list.slice(a, b).map(card).join('')}</div>${pagerHtml(p, pages)}` : empty;
  }
  return `${filterBar(list.length)}${body}${selectionBar()}`;
}

// ---------------- team tab ----------------
export function teamPane(): string {
  const t = S.trainer!;
  const lead = leadCreature();
  const slots = Array.from({ length: CONFIG.teamSize }, (_, i) => {
    const id = t.team[i];
    const c = id !== undefined ? S.creatures.get(id) : undefined;
    if (!c) return `<button class="tcard empty" data-act="team-pick" data-key="e${i}"><span class="slotno">${i ? i + 1 : 'Leads'}</span>+ Add a creature</button>`;
    const src = xpSource(c.id, lead?.id);
    const pill = c.id === lead?.id ? '<span class="pill lead">Leading</span>' : src ? `<span class="pill share">${esc(src.text)}</span>` : '';
    return `<div class="tcard${i === 0 ? ' lead' : ''}" draggable="true" data-team-id="${c.id}" data-open="${c.id}" data-key="c${c.id}">
      <span class="slotno">${i ? i + 1 : 'Leads'}</span>
      <button class="x" data-act="team-remove" data-id="${c.id}" aria-label="Move ${esc(cname(c))} to storage"${t.team.length <= 1 ? ' disabled' : ''}>×</button>
      <img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt="">
      <div class="nm">${nameHtml(c)} ${gradeBadge(factsOf(c).score, 'sm')}</div>
      <div class="lv">Lv ${c.level}${ascendMark(c)}</div>${pill}</div>`;
  }).join('');
  return `<div class="tn-sec"><h3>Team order</h3><span class="spacer"></span><button class="btn small" data-act="team-build">Change team</button></div>
    <div class="tcards">${slots}</div>
    <div id="shrines">${shrinesSection()}</div>`;
}

const tierLine = (tier: number) => { const x = SHRINE_TIERS[tier]; return `${x.name} · +${Math.round(x.share * 100)}% XP · ${x.posts} ${x.posts > 1 ? 'slots' : 'slot'}`; };

function shrinesSection() {
  const t = S.trainer!;
  const shrines = t.shrines ?? [];
  const using = shrines.filter((s) => s.active).sort((a, b) => b.tier - a.tier);
  const idle = shrines.filter((s) => !s.active).sort((a, b) => b.tier - a.tier);
  const shards = t.bag['shrine-shard'] ?? 0;
  const lead = leadCreature();
  const kill = GROUND[t.ground] ? killXp(GROUND[t.ground].level) : 0;
  const cap = using.length >= SHRINES_IN_USE;
  const bench = t.team.filter((id) => id !== lead?.id).length;
  const slotsInUse = using.reduce((a, s) => a + s.posts.length, 0);
  const fusable = SHRINE_TIERS.map((tier, i) => ({ tier, i, n: shrines.filter((s) => s.tier === i).length })).filter((x) => x.tier.fusable && x.n >= SHRINES_PER_FUSE);
  const slot = (s: Shrine, p: number) => {
    const id = s.posts[p];
    const c = id ? S.creatures.get(id) : undefined;
    if (!c) return `<div class="sslot"><button class="btn small" data-act="shrine-pick" data-id="${s.id}" data-qty="${p}">Choose a bench creature</button></div>`;
    return `<div class="sslot"><img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt="" data-creature-id="${c.id}"><span class="grow"><b>${esc(cname(c))}</b> <span class="muted">Lv ${c.level}</span> ${gradeBadge(factsOf(c).score, 'sm')}</span>
      <button class="btn tiny" data-act="shrine-pick" data-id="${s.id}" data-qty="${p}">Change</button><button class="btn tiny" data-act="shrine-clear" data-id="${s.id}" data-qty="${p}">Clear</button></div>`;
  };
  const state = (s: Shrine) => s.posts.map((id, p) => {
    const c = id ? S.creatures.get(id) : undefined;
    if (!c) return '';
    if (!t.team.includes(c.id)) return `<div class="state warn">Not on team · <button class="linkbtn" data-act="team-add" data-id="${c.id}">Add to team</button> / <button class="linkbtn" data-act="shrine-clear" data-id="${s.id}" data-qty="${p}">Clear</button></div>`;
    if (c.id === lead?.id) return `<div class="state sub">Leading · paused</div>`;
    return `<div class="state good">+${full(Math.floor(kill * SHRINE_TIERS[s.tier].share))} XP/kill</div>`;
  }).join('');
  const shrineCard = (s: Shrine) => `<div class="scard t${s.tier}" data-key="${s.id}">
      <div class="row between"><b>${esc(tierLine(s.tier))}</b>${s.dev ? '<span class="muted">Admin-made</span>' : ''}</div>
      ${s.posts.map((_, p) => slot(s, p)).join('')}
      ${state(s)}
      <div class="row"><button class="btn small" data-act="shrine-use" data-id="${s.id}">Stop using</button>${s.dev ? '' : `<button class="btn small" data-act="list-shrine" data-id="${s.id}">Sell…</button>`}</div></div>`;
  const charms = t.bag['xp-charm'] ?? 0;
  return `<div class="shead"><h3>Shrines</h3><span class="muted">${using.length} of ${SHRINES_IN_USE} in use</span>
      <span class="shards"><img src="${iconUrl('shrine-shard')}" alt=""> Shrine Shards ${full(shards)} / ${SHARDS_PER_SHRINE}</span>
      <button class="btn small gold" data-act="shrine-craft"${shards >= SHARDS_PER_SHRINE ? '' : ' disabled'}>Build a shrine</button>
      <span class="spacer"></span><a class="link" href="#/help/shrines">How shrines work</a></div>
    ${using.length ? `<div class="scards">${using.map(shrineCard).join('')}</div>` : `<p class="hint">${shrines.length ? 'None in use.' : 'No shrines.'}</p>`}
    ${idle.length ? `<details class="idle"${using.length ? '' : ' open'}><summary>Not in use (${idle.length})</summary>${idle.map((s) => `<div class="item" data-key="${s.id}"><div class="grow"><b>${esc(tierLine(s.tier))}</b>${s.dev ? ' <span class="muted">admin-made</span>' : ''}</div>
      <button class="btn small primary" data-act="shrine-use" data-id="${s.id}"${cap ? ' disabled' : ''}>Use</button>${s.dev ? '' : `<button class="btn small" data-act="list-shrine" data-id="${s.id}">Sell…</button>`}</div>`).join('')}
      ${cap ? `<p class="hint">${SHRINES_IN_USE}/${SHRINES_IN_USE} in use</p>` : ''}</details>` : ''}
    ${fusable.map((x) => `<div class="item fuse"><div class="grow">Fuse ${SHRINES_PER_FUSE} ${esc(x.tier.name)}s into 1 ${esc(SHRINE_TIERS[x.i + 1].name)} <span class="muted">(${x.n})</span></div><button class="btn small" data-act="shrine-fuse" data-id="${x.i}">Fuse…</button></div>`).join('')}
    <p class="hint">Build odds: ${SHRINE_TIERS.map((x, i) => `${x.name.replace(' Shrine', '')} ${+(shrineChance(i) * 100).toFixed(3)}%`).join(' · ')}</p>
    ${slotsInUse > bench ? `<p class="hint warn-text">Only ${bench} bench ${bench === 1 ? 'creature' : 'creatures'} to fill ${slotsInUse} slots</p>` : ''}
    ${charms ? `<p class="hint">XP Charms: ${charms} spare</p>` : ''}`;
}

// ---------------- pickers and dialogs ----------------
function pickerCard(c: CreatureDTO, note = '', disabled = false) {
  return `<button class="pcard" data-dlg="${disabled ? '' : c.id}"${disabled ? ' disabled' : ''}><img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt=""><span class="grow"><b>${esc(cname(c))}</b> ${gradeBadge(factsOf(c).score, 'sm')}<br><span class="muted">Lv ${c.level}${note ? ` · ${note}` : ''}</span></span></button>`;
}

async function pickForTeam() {
  const t = S.trainer!;
  const pool = [...S.creatures.values()].filter((c) => !t.team.includes(c.id) && !c.listed).sort((a, b) => factsOf(b).power - factsOf(a).power).slice(0, 60);
  if (!pool.length) return toast('Storage empty', 'err');
  const r = await openDialog({ title: 'Add a creature to the team', body: `<div class="picker" data-fit="dlg-team">${pool.map((c) => pickerCard(c, `Power ${full(factsOf(c).power)}`)).join('')}</div>`, actions: [] });
  if (r.value) send({ t: 'team', ids: [...t.team, Number(r.value)] });
}

async function pickForShrine(shrineId: string, post: number) {
  const t = S.trainer!;
  const s = t.shrines?.find((x) => x.id === shrineId);
  if (!s) return;
  const lead = leadCreature();
  const team = t.team.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  const share = SHRINE_TIERS[s.tier].share;
  const r = await openDialog({
    title: `Choose for the ${SHRINE_TIERS[s.tier].name}`,
    body: `<div class="picker" data-fit="dlg-shrine">${team.map((c) => {
      if (c.id === lead?.id) return pickerCard(c, 'Leading', true);
      const src = xpSource(c.id, lead?.id);
      const note = src ? (src.share > share ? `On a better slot (${src.text})` : `Now: ${src.text}`) : 'No XP now';
      return pickerCard(c, note);
    }).join('')}</div>`,
    actions: [],
  });
  if (r.value) send({ t: 'shrine', id: s.id, posts: s.posts.map((x, i) => (i === post ? Number(r.value) : x)) });
}

function miniCards(list: CreatureDTO[]) {
  return `<div class="minis">${list.slice(0, 12).map((c) => `<span class="mini"><img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt="">${gradeBadge(factsOf(c).score, 'sm')}</span>`).join('')}${list.length > 12 ? `<span class="more">+${list.length - 12} more</span>` : ''}</div>`;
}

async function releaseMany(ids: number[]) {
  const picked = ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  const rel = picked.filter(releasable);
  if (!rel.length) return;
  const coins = rel.reduce((a, c) => a + releaseCoins(c), 0);
  const skipped = picked.length - rel.length;
  const r = await openDialog({
    title: `Release ${rel.length} ${rel.length === 1 ? 'creature' : 'creatures'}?`,
    body: `${miniCards(rel)}<p><b>+${full(coins)} coins</b> · can't be undone</p>${skipped ? `<p class="sub">${skipped} protected, skipped</p>` : ''}`,
    danger: true,
    actions: [{ label: `Release for ${full(coins)} coins`, value: 'release', kind: 'danger' }],
  });
  if (!r.value) return;
  send({ t: 'release', ids: rel.map((c) => c.id) });
  for (const c of rel) selected.delete(c.id);
  toast(`Released ${rel.length} for ${full(coins)} coins`);
  rerender();
}

const UPTO_OPTS: [Grade | 'all', string][] = [['C', 'C only'], ['B', 'B and below'], ['A', 'A and below'], ['S', 'S and below'], ['all', 'Any grade']];
const KEEP_OPTS = [0, 1, 2, 3, 5, 10];
const isUpTo = (v: unknown): v is Grade | 'all' => UPTO_OPTS.some(([g]) => g === v);

/**
 * Release in bulk (one tool for what were "Clean up storage" and "Release below"): pick the species (all, one or any
 * mix), the grade it goes up to and how many of the best of each to keep. The preview counts what goes, per species,
 * as the choices change; "Review them" selects them in storage instead.
 */
async function bulkRelease() {
  const t = S.trainer!;
  const owned = new Map<string, number>();
  for (const c of S.creatures.values()) owned.set(c.species, (owned.get(c.species) ?? 0) + 1);
  const species = [...owned.keys()].sort((a, b) => owned.get(b)! - owned.get(a)! || SPECIES[a].name.localeCompare(SPECIES[b].name));
  const start = F.species && owned.has(F.species) ? new Set([F.species]) : null;
  const opts = (d: Record<string, string>): BulkRelease => ({
    species: new Set(species.filter((id) => d[`sp:${id}`] === '1')),
    upTo: isUpTo(d.upTo) ? d.upTo : 'C',
    keep: KEEP_OPTS.includes(Number(d.keep)) ? Number(d.keep) : 1,
    keepBl3: d.bl3 === '1',
  });
  const listFor = (d: Record<string, string>) => bulkReleaseList(t, S.creatures.values(), opts(d));
  const chip = (id: string) => `<label class="pcard alp-c bk-sp"><input type="checkbox" name="sp:${id}"${!start || start.has(id) ? ' checked' : ''}><img src="${spriteUrl(myArt(id, false), false)}" alt=""><span class="grow"><b>${esc(SPECIES[id].name)}</b><br><span class="muted" data-n="${id}"></span></span></label>`;
  let wired = false;
  const r = await openDialog({
    title: 'Release creatures',
    body: `<div class="bk-opts">
        <label class="dlg-field"><span>Grade</span><select name="upTo">${UPTO_OPTS.map(([v, n]) => `<option value="${v}"${v === 'C' ? ' selected' : ''}>${n}</option>`).join('')}</select></label>
        <label class="dlg-field"><span>Keep the best</span><select name="keep">${KEEP_OPTS.map((n) => `<option value="${n}"${n === 1 ? ' selected' : ''}>${n}</option>`).join('')}</select> <span class="sub">of each species</span></label>
        <label class="bk-check"><input type="checkbox" name="bl3" checked> Keep Bloodline ${CONFIG.bloodline[2].name} and up</label>
      </div>
      <div class="alp-head bk-head"><b>Species</b><span class="muted" data-sp-n></span><button type="button" class="linkbtn" data-bk="all">All</button><button type="button" class="linkbtn bk-none" data-bk="none">None</button></div>
      <div class="picker alp bk-grid" data-fit="dlg-bulk">${species.map(chip).join('')}</div>
      <p class="fee rb-count" data-count></p>
      <p class="sub">Always kept: ${KEPT_WHAT}</p>`,
    danger: true,
    actions: [{ label: 'Review them', value: 'show' }, { label: 'Release', value: 'release', kind: 'danger' }],
    onInput: (root, d) => {
      if (!wired) {
        wired = true;
        root.addEventListener('click', (e) => {
          const b = (e.target as HTMLElement).closest<HTMLElement>('[data-bk]');
          if (!b) return;
          for (const box of root.querySelectorAll<HTMLInputElement>('.bk-sp input')) box.checked = b.dataset.bk === 'all';
          root.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }
      const list = listFor(d);
      const per = new Map<string, number>();
      for (const c of list) per.set(c.species, (per.get(c.species) ?? 0) + 1);
      // Each species shows how many go of how many you have, the same rules counted for it alone when it is unticked.
      const all = bulkReleaseList(t, S.creatures.values(), { ...opts(d), species: 'all' });
      const would = new Map<string, number>();
      for (const c of all) would.set(c.species, (would.get(c.species) ?? 0) + 1);
      for (const el of root.querySelectorAll<HTMLElement>('[data-n]')) {
        const id = el.dataset.n!, on = d[`sp:${id}`] === '1', n = would.get(id) ?? 0;
        el.textContent = on ? `${full(n)} of ${full(owned.get(id)!)} go` : `${full(owned.get(id)!)} · all kept`;
        el.closest('.alp-c')!.classList.toggle('on', on);
        el.closest('.alp-c')!.classList.toggle('zero', on && !n);
      }
      const ticked = species.filter((id) => d[`sp:${id}`] === '1').length;
      root.querySelector('[data-sp-n]')!.textContent = `${full(ticked)} of ${full(species.length)}`;
      const coins = list.reduce((a, c) => a + releaseCoins(c), 0);
      root.querySelector('[data-count]')!.textContent = list.length ? `${full(list.length)} ${list.length === 1 ? 'creature' : 'creatures'} · ${full(per.size)} ${per.size === 1 ? 'species' : 'species'} · ${full(coins)} coins` : 'Nothing to release';
      const btns = [...root.querySelectorAll<HTMLButtonElement>('.actions button')];
      const rel = btns[btns.length - 1], show = btns[btns.length - 2];
      rel.disabled = show.disabled = !list.length;
      rel.textContent = list.length ? `Release ${full(list.length)} for ${full(coins)} coins` : 'Release';
    },
  });
  if (!r.value) return;
  const list = listFor(r.data);
  if (!list.length) return;
  if (r.value === 'show') {
    selecting = true; selected.clear();
    for (const c of list) selected.add(c.id);
    // Show exactly them: clear filters that could hide some.
    clearFilters();
    if (location.hash !== '#/creatures') location.hash = '#/creatures';
    rerender();
    return;
  }
  // Good ones going (graded S or better): one more look first.
  const good = list.filter((c) => GRADE_ORDER.indexOf(factsOf(c).grade) >= GRADE_ORDER.indexOf('S'));
  if (good.length) {
    const ok = await openDialog({ title: `Release ${full(good.length)} graded S or better?`, body: `${miniCards(good)}<p>They go with the rest. Can't be undone.</p>`, danger: true, actions: [{ label: `Release all ${full(list.length)}`, value: 'go', kind: 'danger' }] });
    if (!ok.value) return;
  }
  const coins = list.reduce((a, c) => a + releaseCoins(c), 0);
  send({ t: 'release', ids: list.map((c) => c.id) });
  for (const c of list) selected.delete(c.id);
  toast(`Released ${full(list.length)} for ${full(coins)} coins`);
}

/** Of these, the ones the Altar takes (shared/altar.ts offerable). */
const altarPick = (cs: CreatureDTO[]) => offerable(S.trainer!, cs);

/** The wheel for these creatures as the Altar shows it: the chance of any item, then each item's. */
function altarOddsHtml(pick: CreatureDTO[]) {
  const odds = altarOdds(pick, S.trainer!.level);
  const items = odds.items.map((x) => `<span class="al-item"><img src="${iconUrl(x.item)}" alt="">${esc(ITEM[x.item].name)} <b>${pct(x.p)}</b></span>`).join('');
  return `<div class="al-odds"><span class="al-chance">${pct(odds.chance)}</span>${items}</div>`;
}
const pct = (p: number) => `~${Math.round(p * 100)}%`;

/** The Altar: the chosen creatures (1 to 5, none protected), or else the five lowest graded; the chance shown is the server's formula. */
export async function altar(ids: number[] = []) {
  const t = S.trainer!;
  if (t.level < ALTAR.minLevel) { toast(`The Altar opens at level ${ALTAR.minLevel}`); return; }
  const chosen = altarPick(ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c));
  const pick = chosen.length && chosen.length <= ALTAR.slots ? chosen : worstOfferable(t, S.creatures.values());
  const left = altarLeft(t, Date.now());
  const r = await openDialog({
    title: 'Altar',
    body: pick.length ? `${miniCards(pick)}${altarOddsHtml(pick)}
      <p class="sub">${chosen.length ? 'Chosen' : 'Lowest graded'} · ${left} of ${ALTAR.weekly} left this week · creatures are used up</p>` : '<p class="sub">Nothing in storage can be offered.</p>',
    danger: true,
    actions: [
      ...(pick.length ? [{ label: 'Choose creatures', value: 'choose' }] : []),
      { label: `Offer ${pick.length}`, value: 'offer', kind: 'danger', disabled: !pick.length || left <= 0 },
    ],
  });
  if (r.value === 'choose') {
    const next = await chooseForAltar(pick.map((c) => c.id));
    return altar(next ?? chosen.map((c) => c.id));
  }
  if (r.value !== 'offer' || !pick.length) return;
  send({ t: 'altar', ids: pick.map((c) => c.id) });
  for (const c of pick) selected.delete(c.id);
}

/** How many creatures the Altar picker lists at once (lowest graded first); a search finds the rest. */
const ALTAR_PICKER_ROWS = 60;

/**
 * The Altar's picker: up to ALTAR.slots of the creatures it takes, lowest graded first, with the page's search.
 * Each card shows what that creature adds (its chance and the items it can pay); the wheel updates as you tick.
 * Resolves the ids, or null on Cancel.
 */
async function chooseForAltar(start: number[]): Promise<number[] | null> {
  const t = S.trainer!;
  const all = worstOfferable(t, S.creatures.values(), Infinity);
  const ok = new Set(all.map((c) => c.id));
  const chosen = new Set(start.filter((id) => ok.has(id)));
  let q: string | null = null;
  const card = (c: CreatureDTO) => {
    const o = altarOdds([c], t.level), on = chosen.has(c.id);
    const pays = o.items.map((x) => `<img class="ico16" src="${iconUrl(x.item)}" alt="${esc(ITEM[x.item].name)}" title="${esc(ITEM[x.item].name)}">`).join('');
    return `<label class="pcard alp-c${on ? ' on' : ''}"><input type="checkbox" data-alp="${c.id}"${on ? ' checked' : ''}><img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt=""><span class="grow"><b>${esc(cname(c))}</b> ${gradeBadge(factsOf(c).score, 'sm')}<br><span class="muted">Lv ${c.level} · +${Math.round(o.chance * 100)}% ${pays}</span></span></label>`;
  };
  const grid = (root: HTMLElement) => {
    const list = q ? all.filter((c) => searchMatches(c, q!)) : all;
    const shown = list.slice(0, ALTAR_PICKER_ROWS);
    root.querySelector('[data-grid]')!.innerHTML = shown.length ? shown.map(card).join('') : '<p class="hint">Nothing matches.</p>';
    root.querySelector('[data-more]')!.textContent = list.length > shown.length ? `The ${ALTAR_PICKER_ROWS} lowest graded of ${full(list.length)}. Search for the rest.` : '';
  };
  const sync = (root: HTMLElement) => {
    const full5 = chosen.size >= ALTAR.slots;
    for (const box of root.querySelectorAll<HTMLInputElement>('[data-alp]')) {
      box.disabled = !box.checked && full5;
      box.closest('.alp-c')!.classList.toggle('on', box.checked);
    }
    const picked = all.filter((c) => chosen.has(c.id));
    root.querySelector('[data-sum]')!.innerHTML = `<div class="alp-head"><b>${chosen.size} of ${ALTAR.slots}</b>${chosen.size ? '<button type="button" class="linkbtn" data-alp-clear>Clear</button>' : '<span class="muted">Tick up to five</span>'}</div>${picked.length ? altarOddsHtml(picked) : ''}`;
    const done = [...root.querySelectorAll<HTMLButtonElement>('.actions button')].pop()!;
    done.disabled = !chosen.size;
  };
  let wired = false;
  const p = openDialog({
    title: 'Choose for the Altar',
    body: `<label class="fsearch alp-search">${ICON.search}<input name="q" placeholder="Name, species or element" aria-label="Search by name, species or element"></label>
      <div data-sum></div><div class="picker alp" data-grid data-fit="dlg-altar"></div><p class="hint" data-more></p>`,
    danger: true,
    actions: [{ label: 'Done', value: 'done', kind: 'primary' }],
    onInput: (root, d) => {
      if (!wired) {
        wired = true;
        root.addEventListener('click', (e) => {
          if (!(e.target as HTMLElement).closest('[data-alp-clear]')) return;
          chosen.clear();
          for (const box of root.querySelectorAll<HTMLInputElement>('[data-alp]')) box.checked = false;
          sync(root);
        });
      }
      // Ticks in the cards shown now; a search keeps the picks it hides.
      for (const box of root.querySelectorAll<HTMLInputElement>('[data-alp]')) {
        const id = Number(box.dataset.alp);
        if (box.checked) chosen.add(id); else chosen.delete(id);
      }
      const nq = (d.q ?? '').trim().toLowerCase();
      if (nq !== q) { q = nq; grid(root); }
      sync(root);
    },
  });
  // On a phone, opening with the search focused would pop up the keyboard over the list.
  if (matchMedia('(pointer: coarse)').matches) (document.activeElement as HTMLElement | null)?.blur();
  const r = await p;
  return r.value === 'done' && chosen.size ? [...chosen] : null;
}

function clearFilters() {
  F.grades.clear(); F.blMin = 0; F.aura = false; F.fav = false; F.species = ''; F.element = ''; F.since = 0; S.depotFilter = '';
}

// ---------------- events ----------------
/** Clicks on this page. Returns true when handled. */
export function creaturesClick(e: MouseEvent, el: HTMLElement): boolean {
  const id = el.dataset.id ?? '';
  const t = S.trainer!;
  switch (el.dataset.act) {
    case 'cf-grade': { const g = id as Grade; if (F.grades.has(g)) F.grades.delete(g); else F.grades.add(g); break; }
    case 'cf-aura': F.aura = !F.aura; break;
    case 'cf-page': cPage = Number(id) || 0; break;
    case 'cf-fav': F.fav = !F.fav; break;
    case 'cf-clear': clearFilters(); break;
    case 'cf-since': F.since = 0; break;
    case 'cf-view': view = id === 'list' ? 'list' : 'grid'; try { localStorage.setItem('mi-creatures-view', view); } catch { /* storage off */ } break;
    case 'cf-col': { const k = id as SortKey; if (F.sort === k) F.asc = !F.asc; else { F.sort = k; F.asc = false; } break; }
    case 'cf-more': filtersOpen = !filtersOpen; break;
    case 'cf-select': selecting = !selecting; if (!selecting) selected.clear(); break;
    case 'sel-all': for (const c of depotList()) selected.add(c.id); break;
    case 'sel-clear': selected.clear(); break;
    case 'sel-fav': {
      const picked = [...selected].map((x) => S.creatures.get(x)).filter((c): c is CreatureDTO => !!c);
      const on = !picked.every(isFavorite);
      for (const c of picked) if (isFavorite(c) !== on) send({ t: 'collect', id: c.id, on });
      return true;
    }
    case 'sel-team': {
      const free = CONFIG.teamSize - t.team.length;
      const add = [...selected].filter((x) => { const c = S.creatures.get(x); return c && !t.team.includes(x) && !c.listed; }).slice(0, free);
      if (add.length) send({ t: 'team', ids: [...t.team, ...add] });
      return true;
    }
    case 'sel-list': { const [one] = [...selected]; if (one) listCreature(one); return true; }
    case 'sel-release': void releaseMany([...selected]); return true;
    case 'bulk-release': void bulkRelease(); return true;
    case 'altar': void altar(); return true;
    case 'sel-altar': void altar([...selected]); return true;
    case 'team-pick': void pickForTeam(); return true;
    case 'team-build':
      openTeamBuilder({
        title: 'Hunt team', ids: S.trainer!.team, max: CONFIG.teamSize, min1: true, name: 'hunt', key: '', ok: (c) => !c.listed, asFought: asFighter,
        onSave: (ids) => { if (ids.length) send({ t: 'team', ids }); },
      });
      return true;
    case 'shrine-pick': void pickForShrine(id, Number(el.dataset.qty)); return true;
    default: return false;
  }
  rerender();
  return true;
}

/** In select mode a click on a creature toggles it (shift: the range from the last one) instead of opening it. */
export function creaturesOpen(e: MouseEvent, id: number): boolean {
  if (!selecting || S.page !== 'creatures' || S.tab === 'team') return false;
  if (e.shiftKey && anchorId !== null) {
    const list = depotList().map((c) => c.id);
    const a = list.indexOf(anchorId), b = list.indexOf(id);
    if (a >= 0 && b >= 0) for (let i = Math.min(a, b); i <= Math.max(a, b); i++) selected.add(list[i]);
  } else if (selected.has(id)) selected.delete(id);
  else selected.add(id);
  anchorId = id;
  rerender();
  return true;
}

export function creaturesChange(el: HTMLInputElement | HTMLSelectElement): boolean {
  const k = el.dataset.cf;
  if (!k) return false;
  if (k === 'blMin') F.blMin = Number(el.value);
  else if (k === 'species') F.species = el.value;
  else if (k === 'element') F.element = el.value;
  else if (k === 'sort') { F.sort = el.value as SortKey; F.asc = false; }
  rerender();
  return true;
}

/** Ctrl/Cmd-A in select mode selects everything that matches the filters. */
export function creaturesKey(e: KeyboardEvent): boolean {
  if (!selecting || S.page !== 'creatures' || S.tab === 'team') return false;
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
    e.preventDefault();
    for (const c of depotList()) selected.add(c.id);
    rerender();
    return true;
  }
  return false;
}

/** Forget selected creatures that are gone (released, sold). */
export function pruneSelection() { for (const id of selected) if (!S.creatures.has(id)) selected.delete(id); }

/** The away card's "See new catches": storage filtered to catches since a time, newest first. */
export function showCaughtSince(ts: number) {
  clearFilters();
  F.since = ts;
  F.sort = 'recent'; F.asc = false;
}
